import React from 'react';
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from 'react-router-dom';
import AppLayout from '@amzn/meridian/app-layout';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Masthead, { MastheadLink, MastheadTitle } from '@amzn/meridian/masthead';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { Logger } from 'src/utils/logger';
import { METRIC, routerMetricsPublisher } from 'src/utils/metrics';

const BasicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <AppLayout headerComponent={Masthead}>
      <Masthead size="small">
        <MastheadTitle>
          <MastheadLink href="/" onClick={navigate}>
            <Text>{t('leela_title')}</Text>
          </MastheadLink>
        </MastheadTitle>
      </Masthead>
      <Column
        alignmentHorizontal="center"
        alignmentVertical="center"
        backgroundColor="subdued"
        minHeight="100%"
        spacingInset="500 600"
      >
        {children}
      </Column>
    </AppLayout>
  );
};

/**
 * Catch all error boundary. Since errors bubble up to parent, you can still add
 * more granular error boundaries in children.
 *
 * https://reactrouter.com/en/main/route/error-element
 */
export const GlobalErrorBoundary = () => {
  const { t } = useTranslation();
  const routeError = useRouteError();

  if (isRouteErrorResponse(routeError)) {
    const { status, statusText } = routeError;
    const statusMessage = `${status} ${statusText}`;

    // @ts-expect-error Actually has `error` property
    const error = routeError.error as Error;

    if (routeError.status === 404) {
      Logger.info(statusMessage, error);
      routerMetricsPublisher.publishCounter(METRIC.NotFound, 1);
    } else {
      // In theory we shouldn't see these as I think 404 is the only error
      // react-router automatically throws for us.
      Logger.error(statusMessage, error);
      routerMetricsPublisher.publishCounter(METRIC.Error, 1);
    }

    return (
      <BasicLayout>
        <Heading level={1}>{t('general_error')}</Heading>
        <Heading data-testid="router-error-status" level={2}>
          {statusMessage}
        </Heading>
      </BasicLayout>
    );
  }

  // In dev server `LeelaUILogger` will also log this. However, in prod this
  // will be logged by the error boundary.
  Logger.fatal('Application Error', routeError as Error);
  routerMetricsPublisher.publishCounter(METRIC.Fatal, 1);

  return (
    <BasicLayout>
      <Heading data-testid="application-error-heading" level={1}>
        {t('general_error')}
      </Heading>
    </BasicLayout>
  );
};
